import {
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Zoom
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import {
  CONST_BUSINESS_HOST,
  CONST_ONBOARD_AZURE_CUSTOM_ROLE_JSON,
  CONST_SITE_LABEL,
  ONBOARDING_STEPS_CASE,
  STORAGE_KEYS,
  navigateToDashboardPostLogin,
  setScopedItem,
} from './common/Helper';
import './common/Styles.css'
import './common/OnboardingStyles.css'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import RegistrationApiService from '../services/RegistrationApiService';
import DebugService from '../services/DebugService';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

// Modern Icons for Azure
import {
  Security as SecurityIcon,
  Build as ManualModeIcon,
  Link as LinkIcon,
  Cloud as AzureIcon,
  AppRegistration as AppRegistrationIcon,
  AutoFixHigh as AutomationIcon,
  CloudUpload as DeploymentIcon,
  Settings as ConfigurationIcon,
  CheckCircle as VerificationIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export const Step1Details = () => {
  return (
    <>
      <Typography className='step-details-heading'>Step 1: Go to "Microsoft Entra ID"</Typography>
      <Typography className='step-details-text'>Make sure you have the right permissions.</Typography>

      <Typography className='step-details-heading margin-top'>Step 2: Click "App Registrations"</Typography>

      <Typography className='step-details-heading margin-top'>Step 3: Register the new app by clicking "New Registration"</Typography>

      <Typography className='step-details-heading margin-top'>Step 4: Give your application a name</Typography>
      <Typography className='step-details-text'>We recommend you use "${CONST_SITE_LABEL}-Integrations" for the name.</Typography>

      <Typography className='step-details-heading margin-top'>Step 5: Set the sign-on URL</Typography>
      <Typography className='step-details-text'>In Redirect URL select <span style={{ fontWeight: 600 }}>Web</span> and
        add <a
          href={CONST_BUSINESS_HOST}
          target='_blank'
          rel="noreferrer"
          title='Click to open'
        >
          {CONST_BUSINESS_HOST}
        </a> as the sign-on URL.
      </Typography>

      <Typography className='step-details-heading margin-top'>Step 6: Click "Register" to create the application</Typography>
      <Typography className='step-details-heading margin-top'>Step 7: Generate and Add a New Client Secret for the Application</Typography>
      <Typography className='step-details-text'>Open the newly created application, navigate to <span style={{ fontWeight: 600 }}>"Certificates & secrets"</span>, and select <span style={{ fontWeight: 600 }}>"New client secret"</span>. Enter a description and set the expiration period to 24 months, then click "Add" to generate the client secret.</Typography>
    </>
  )
}

export const Step2Details = ({roles}) => {
  return (
    <>
      <Typography className='step-details-heading'>Step 1: Go to the "Subscriptions" section in Azure</Typography>

      <Typography className='step-details-heading margin-top'>Step 2: Select the subscription you want to connect to {CONST_SITE_LABEL}</Typography>
      <Typography className='step-details-text'>Once you select the subscription, you'll see the available options.</Typography>

      <Typography className='step-details-heading margin-top'>Step 3: Click "Access control (IAM)"</Typography>

      <Typography className='step-details-heading margin-top'>Step 4: Create a Custom Role</Typography>
      <Typography className='step-details-text'>
        - Under Roles, click + Add → Add custom role.<br/>
        - Download the JSON file from: <a href={CONST_ONBOARD_AZURE_CUSTOM_ROLE_JSON} target="_blank" rel="noopener noreferrer">{process.env.REACT_APP_AZURE_CUSTOM_ROLE_JSON}</a><br/>
        - In the Baseline section, select Start from JSON and upload the downloaded file.<br/>
        - After uploading, go to the Assignable scopes tab.<br/>
        - Click + Add assignable scope and select your subscription.<br/>
        - Click Review + Create, then Create.
      </Typography>
      
      <Typography className='step-details-heading margin-top'>Step 5: In Access control (IAM), Click "Add" and select "Add role assignment"</Typography>
      
      <Typography className='step-details-heading margin-top'>Step 6: Assign Built‑in Roles under "Job function roles": {roles.join(', ')}</Typography>

      <Typography className='step-details-heading margin-top'>Step 7: Assign Custom Role under "Job function roles"</Typography>
      <Typography className='step-details-text'>Select the custom role you created in Step 4 (DB Flexible Server Operator) and click Next</Typography>

      <Typography className='step-details-heading margin-top'>Step 8: Make sure your application appears under "Selected members" and save</Typography>
      <Typography className='step-details-text'>Under <span style={{ fontWeight: 600 }}>Members</span>, click <span style={{ fontWeight: 600 }}>Select Members</span> and enter the name of the application you created (if you followed the recommendation, it's <span style={{ fontWeight: 600 }}>{CONST_SITE_LABEL}-Integrations</span>).</Typography>
    </>
  )
}

export const Step3Details = (props) => {
  const [linkForm, setLinkForm] = useState(props['linkForm'])

  const onFieldChange = useCallback((fieldName, value) => {
    setLinkForm(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }, [])

  useEffect(() => {
    if (linkForm['accountAliase'] !== '' && linkForm['accountId'] !== '' && linkForm['azSubscriptionId'] !== '' && linkForm['azClientId'] !== '' && linkForm['azClientSecret'] !== '') {
      props['onFormUpdate'](linkForm)
    }
  }, [linkForm])

  return (
    <>
      {/* Manual step details are handled in the main component form */}
    </>
  )
}

// Automated Step Components
export const AutomatedStep1Details = ({ deviceCode, onConfirmationChange, roles }) => {
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsConfirmed(checked);
    if (onConfirmationChange) {
      onConfirmationChange(checked);
    }
  };

  return (
    <>
      <Typography className='step-details-heading'>Azure Authentication Required</Typography>
      <Typography className='step-details-text'>
        We are only requesting Reader access along with permissions to start/stop virtual machines and manage auto-scaling (scale in/out),
        specifically through the following roles: {roles.join(', ')}.
        We want to emphasize that we do not access or read any data, files, or content stored within your Azure environment.
      </Typography>

      <Typography className='step-details-heading margin-top'>Please follow the following steps:</Typography>
      <Typography className='step-details-text'>
        To sign in, use a web browser to open the page{' '}
        <a
          href='https://microsoft.com/devicelogin'
          target='_blank'
          rel='noopener noreferrer'
          className='azure-auth-link'
        >
          https://microsoft.com/devicelogin
        </a>
        {' '}and enter the code to authenticate.
      </Typography>

      <Typography className='step-details-text margin-top azure-device-code-text'>
        Your Code is - <span className='azure-device-code'>{deviceCode}</span>
      </Typography>

      <div className='azure-confirmation-container'>
        <input
          type="checkbox"
          id="azure-login-confirmation"
          checked={isConfirmed}
          onChange={handleCheckboxChange}
          className='azure-confirmation-checkbox'
        />
        <label
          htmlFor="azure-login-confirmation"
          className='azure-confirmation-label'
        >
          To confirm that User has login to the Azure console using above steps.
        </label>
      </div>
    </>
  )
}

export const AutomatedStep2Details = ({ subscriptions, selectedSubscription, onSubscriptionSelect, isLoading }) => {
  return (
    <>
      <Typography className='step-details-heading'>Select Your Azure Subscription</Typography>
      <Typography className='step-details-text'>
        Please select the Azure subscription you want to integrate with our FinOps platform.
        We'll configure the necessary permissions for cost management and optimization.
      </Typography>

      {isLoading ? (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <LinearProgress
            sx={{
              width: '100%',
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(45deg, #0078d4 30%, #106ebe 90%)',
                borderRadius: 4
              }
            }}
          />
          <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
            Loading your Azure subscriptions...
          </Typography>
        </div>
      ) : (
        <div className='azure-content-container'>
          {subscriptions && subscriptions.length > 0 ? (
            <div>
              <Typography className='step-details-heading margin-top'>Available Subscriptions:</Typography>
              <div className='azure-subscription-list'>
                {subscriptions.map((subscription, index) => (
                  <Card
                    key={subscription.subscriptionId || index}
                    className={`azure-subscription-card ${
                      selectedSubscription?.subscriptionId === subscription.subscriptionId ? 'selected' : ''
                    }`}
                    onClick={() => onSubscriptionSelect(subscription)}
                  >
                    <div className='azure-subscription-content'>
                      <div className={`azure-subscription-radio ${
                        selectedSubscription?.subscriptionId === subscription.subscriptionId ? 'selected' : ''
                      }`} />
                      <div className='azure-subscription-info'>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
                          {subscription.subscriptionName ?? `Subscription ${index + 1}`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                          ID: {subscription.subscriptionId}
                        </Typography>
                        {/* {subscription.state && (
                          <Chip
                            label={subscription.state}
                            size="small"
                            color={subscription.state === 'Enabled' ? 'success' : 'default'}
                            sx={{ mt: 1, fontSize: '0.75rem' }}
                          />
                        )} */}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Card className='azure-no-subscriptions'>
              <Typography variant="h6" sx={{ color: '#92400e', mb: 1 }}>
                No Subscriptions Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#92400e' }}>
                We couldn't find any Azure subscriptions associated with your account.
                Please ensure you have the necessary permissions and try again.
              </Typography>
            </Card>
          )}
        </div>
      )}

      <Typography className='step-details-text margin-top' sx={{ fontSize: '0.875rem', color: '#64748b' }}>
        <strong>Note:</strong> We'll only request Reader permissions along with limited VM management capabilities.
        No data or files within your Azure environment will be accessed.
      </Typography>
    </>
  )
}

export const AutomatedStep3Details = ({ accountData, accountName, onAccountNameChange, isLoading }) => {
  return (
    <>
      <Typography className='step-details-heading'>Complete Account Setup</Typography>
      <Typography className='step-details-text'>
        Please provide a friendly name for your Azure account and review the connection details below.
      </Typography>

      {isLoading ? (
        <div className='azure-loading-container'>
          <LinearProgress
            sx={{
              width: '100%',
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e2e8f0',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(45deg, #0078d4 30%, #106ebe 90%)',
                borderRadius: 4
              }
            }}
          />
          <Typography variant="body2" sx={{ mt: 2, color: '#64748b' }}>
            Setting up your Azure account details...
          </Typography>
        </div>
      ) : (
        <div className='azure-content-container-large'>
          {/* Account Name Input */}
          <Card className='azure-account-name-card'>
            {/* <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 600 }}>
              Account Information
            </Typography> */}
            <TextField
              required
              fullWidth
              variant="outlined"
              label="Account Name"
              placeholder="Enter a friendly name for your Azure account"
              value={accountName}
              onChange={(e) => onAccountNameChange(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: '#0078d4'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0078d4'
                  }
                }
              }}
              helperText="This name will help you identify this Azure account in the dashboard"
            />
          </Card>

          {/* Connection Details */}
          {accountData && (
            <Card className='azure-connection-details-card'>
              <Typography variant="h6" sx={{ mb: 3, color: '#1e293b', fontWeight: 600 }}>
                Connection Details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Subscription ID"
                    value={accountData.subscriptionId || ''}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f1f5f9'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Tenant ID"
                    value={accountData.tenantId || ''}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f1f5f9'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Application ID"
                    value={accountData.applicationId || ''}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f1f5f9'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Client Secret"
                    value={accountData.clientSecretValue ?? ''}
                    InputProps={{
                      readOnly: true,
                      style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f1f5f9'
                      }
                    }}
                    // helperText="Client secret is masked for security"
                  />
                </Grid>
              </Grid>

              {/* Status Information */}
              {/* <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #0ea5e9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <CheckCircle sx={{ color: '#0ea5e9', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0c4a6e' }}>
                    Connection Verified
                  </Typography>
                </div>
                <Typography variant="body2" sx={{ color: '#0c4a6e' }}>
                  Successfully connected to your Azure subscription with the required permissions.
                </Typography>
              </div> */}
            </Card>
          )}
        </div>
      )}

      <Typography className='step-details-text margin-top' sx={{ fontSize: '0.875rem', color: '#64748b' }}>
        <strong>Note:</strong> All connection details are securely stored and encrypted.
        Only the minimum required permissions have been configured for cost optimization.
      </Typography>
    </>
  )
}

/**
 * Azure Onboarding Component
 * @returns 
 */
export const AzureOnboarding = () => {
  const [expanded, setExpanded] = useState(ONBOARDING_STEPS_CASE.AUTOMATION)
  const [isValidating, setIsValidating] = useState(false)
  const [isSubmitDisable, setIsSubmitDisable] = useState(false)
  const [APIErrorMessage, setAPIErrorMessage] = useState('')
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorDialogTitle, setErrorDialogTitle] = useState('')
  const [errorDialogMessage, setErrorDialogMessage] = useState('')
  const [completedStep, setCompletedStep] = useState(0)
  const [isStep1Confirmed, setIsStep1Confirmed] = useState(false)
  const [subscriptions, setSubscriptions] = useState([])
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false)
  const [accountData, setAccountData] = useState(null)
  const [accountName, setAccountName] = useState('')
  const [isLoadingAccountData, setIsLoadingAccountData] = useState(false)
  const [deviceCode, setDeviceCode] = useState(null)
  const [sessionId, setSessionId] = useState(null)
  const [roles, setRoles] = useState([])
  const [linkForm, setLinkForm] = useState({
    accountId: "",
    accountAliase: "",
    azSubscriptionId: "",
    azClientId: "",
    azClientSecret: ""
  })

  const { setIsOnboarded, refetchUserDetails, selectedPlatform } = useAuth()
  const navigate = useNavigate()
  const { getScopedItem, removeScopedItem } = useScopedLocalStorage()

  const stepList = [
    {
      id: 1,
      label: 'Create App'
    },
    {
      id: 2,
      label: 'Add Read Permission'
    },
    {
      id: 3,
      label: 'Add Account Details'
    }
  ]

  const automationStepList = [
    {
      id: 1,
      label: 'Azure Authentication'
    },
    {
      id: 2,
      label: 'Select Subscription'
    },
    {
      id: 3,
      label: 'Complete Setup'
    }
  ]

  /**
   * Accordion change handler
   * @param {*} value 
   */
  const handleChange = (value) => {
    if (value.length > 0) {
      setExpanded(value);
    }
  };

  /**
   * Handle link account
   */
  const linkAccount = () => {
    setIsValidating(true)

    // if (!tempAccountId || !tempAccountARN) {
    //   // Empty field validation
    //   setAPIErrorMessage('Please fill all required details!')
    //   setIsValidating(false)
    // } else if (!validateAccountId(tempAccountId)) {
    //   // Invalid account id
    //   setAPIErrorMessage('Account ID should be 12 digit numeric value')
    //   setIsValidating(false)
    // } else if (!validateAccountARN(tempAccountARN)) {
    //   // Invalid account arn validation
    //   setAPIErrorMessage('Please enter valid Account ARN')
    //   setIsValidating(false)
    // } else {

    if (getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)) {
      const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
      const parseToJson = JSON.parse(userDetails)

      const requestBody = {
        "email": '',
        "userId": 0,
        "platformEnum": selectedPlatform
      }

      if (expanded === ONBOARDING_STEPS_CASE.AUTOMATION) {
        requestBody['accountAliase'] = accountName
        requestBody['accountId'] = accountData['tenantId']
        requestBody['azSubscriptionId'] = accountData['subscriptionId']
        requestBody['azClientId'] = accountData['applicationId']
        requestBody['azClientSecret'] = accountData['clientSecretValue']
      } else {
        requestBody['accountAliase'] = linkForm['accountAliase']
        requestBody['accountId'] = linkForm['accountId']
        requestBody['azSubscriptionId'] = linkForm['azSubscriptionId']
        requestBody['azClientId'] = linkForm['azClientId']
        requestBody['azClientSecret'] = linkForm['azClientSecret']
      }

      if (parseToJson['userId']) {
        requestBody['userId'] = parseToJson['userId']
      }

      if (parseToJson['email']) {
        requestBody['email'] = parseToJson['email']
      }

      DebugService.log(`Azure Onboard Component: Initiated account linking`, requestBody)

      RegistrationApiService.accountLink(requestBody)
        .then(response => {
          if (response['data'] && response['data']['message'] && response['data']['message'].includes('success')) {
            refetchUserDetails()
            setIsOnboarded(true)

            // Navigate to dashboard
            navigateToDashboardPostLogin(navigate, true)

            removeScopedItem('accountId')
            removeScopedItem('accountARN')
            setScopedItem(STORAGE_KEYS.PLATFORM, getScopedItem(STORAGE_KEYS.PLATFORM))
            setScopedItem(STORAGE_KEYS.ACCOUNT_ID, requestBody['accountId'])
            // navigate(CONST_ROUTE_PATH.PRICING)
          }

          if (response['error'] && response['error']['message']) {
            const errorMsg = `${response['error']['message']} - ${response['error']['detail']}`
            setAPIErrorMessage(errorMsg)
            showError(response['error']['message'], response['error']['detail'])
            DebugService.error(`Azure Onboard Component: Caught error while account linking`, response, requestBody)
          }
        })
        .catch(e => {
          const errorMsg = 'An unexpected error occurred while linking your Azure account. Please try again.'
          setAPIErrorMessage(errorMsg)
          showError('Connection Error', errorMsg)
          DebugService.error(`Azure Onboard Component: Caught error while account linking`, e, requestBody)
        })
        .finally(() => {
          setIsValidating(false)
        })
    } else {

    }
    // }
  }

  const nextStepHandle = (isAutomation) => {
    const currentStepList = isAutomation ? automationStepList : stepList
    const lastStep = currentStepList.length - 1

    if (completedStep >= 0 && completedStep < lastStep) {
      const nextStep = completedStep + 1
      setCompletedStep(nextStep)

      // Trigger API call when moving to step 2 (subscription selection)
      if (isAutomation && nextStep === 1 && sessionId !== null) {
        fetchAzureSubscriptions()
      }
      // Trigger API call when moving to step 3 (account setup)
      else if (isAutomation && nextStep === 2) {
        fetchAccountData()
      }
    }
    // Final step - complete setup
    else if (completedStep === lastStep) {
      // if (isAutomation) {
      //   // For automation, simulate completion
      //   handleAutomatedCompletion()
      // } else {
        linkAccount()
      // }
    }
  }

  const backStepHandle = (isAutomation) => {
    const currentStepList = isAutomation ? automationStepList : stepList
    const maxStep = currentStepList.length - 1

    if (completedStep > 0 && completedStep <= maxStep) {
      const prevStep = completedStep - 1
      setCompletedStep(prevStep)
    }
  }

  /**
   * Handle automated completion
   */
  // const handleAutomatedCompletion = () => {
  //   setIsValidating(true)

  //   // Simulate automated deployment process
  //   setTimeout(() => {
  //     // For demo purposes, we'll simulate success
  //     // In real implementation, this would call the ARM template deployment API
  //     setIsOnboarded(true)
  //     navigate(CONST_ROUTE_PATH.PRICING)
  //     setIsValidating(false)
  //   }, 3000)
  // }

  /**
   * On form update handler
   * @param {*} fieldName 
   * @param {*} value 
   */
  const onFormUpdateHandler = useCallback((updatedLinkForm) => {
    setLinkForm(updatedLinkForm)
  }, [])

  /**
   * Handle step 1 confirmation change
   * @param {*} isConfirmed 
   */
  const handleStep1ConfirmationChange = useCallback((isConfirmed) => {
    setIsStep1Confirmed(isConfirmed)
  }, [])

  /**
   * Fetch Azure subscriptions from API
   */
  const fetchAzureSubscriptions = async () => {
    setIsLoadingSubscriptions(true)
    try {

      RegistrationApiService.getAzureAutomateOnboardingStatus({ sessionId: sessionId })
        .then(response => {
          const data = response['data'] ?? {}
          DebugService.log(`Azure Onboard Component: Onboard status`, response)
          if (data['status'] && data['status'] === 'success') {
            RegistrationApiService.getAzureAutomateOnboardingSubscription({ sessionId: sessionId })
              .then(response => {
                const data = response['data'] ?? []
                DebugService.log(`Azure Onboard Component: Onboard subscription list`, response)
                setSubscriptions(data)
                setIsLoadingSubscriptions(false)
              })
              .catch(e => {
                showError('Subscription Loading Failed', 'Failed to load your Azure subscriptions. Please check your connection and try again.')
                DebugService.error(`Azure Onboard Component: Caught error while onboarding status`, e)
              })
          } else {
            setIsLoadingSubscriptions(false)
            showError('Authentication Failed', 'Azure authentication was not successful. Please try the authentication process again.')
            DebugService.error(`Azure Onboard Component: Onboarding status is not successful`,)
          }
        })
        .catch(e => {
          showError('Connection Error', 'Unable to connect to Azure services. Please check your internet connection and try again.')
          DebugService.error(`Azure Onboard Component: Caught error while onboarding status`, e)
        })
        .finally(() => {
          setIsLoadingSubscriptions(false)
        })
    } catch (error) {
      showError('Unexpected Error', 'An unexpected error occurred while fetching subscriptions. Please refresh the page and try again.')
      DebugService.error('Azure Onboard Component: Failed to fetch Azure subscriptions:', error)
      setSubscriptions([])
      setIsLoadingSubscriptions(false)
    }
  }

  /**
   * Handle subscription selection
   * @param {*} subscription 
   */
  const handleSubscriptionSelect = useCallback((subscription) => {
    setSelectedSubscription(subscription)
  }, [])

  /**
   * Fetch account data from API
   */
  const fetchAccountData = async () => {
    setIsLoadingAccountData(true)
    try {
      const dataRequest = {
        sessionId: sessionId,
        subscriptionId: selectedSubscription['subscriptionId'],
        tenantId: selectedSubscription['tenantId']
      }
      RegistrationApiService.getAzureAutomateOnboardingDetails(dataRequest)
        .then(response => {
          const data = response['data'] ?? {}
          DebugService.log(`Azure Onboard Component: Onboard details`, response)
          setAccountData(data)
          setIsLoadingAccountData(false)
        })
        .catch(e => {
          showError('Account Setup Failed', 'Failed to retrieve account details. Please try selecting your subscription again.')
          DebugService.error(`Azure Onboard Component: Caught error while fetching onboarding details`, e)
        })
        .finally(() => {
          setIsLoadingAccountData(false)
        })
    } catch (error) {
      showError('Account Data Error', 'Unable to fetch account data. Please try again or contact support if the issue persists.')
      DebugService.error('Failed to fetch account data:', error)
      setAccountData(null)
      setIsLoadingAccountData(false)
    }
  }

  /**
   * Handle account name change
   * @param {*} name 
   */
  const handleAccountNameChange = useCallback((name) => {
    setAccountName(name)
  }, [])

  /**
   * Show error dialog
   * @param {string} title - Error dialog title
   * @param {string} message - Error message to display
   */
  const showError = useCallback((title, message) => {
    setErrorDialogTitle(title)
    setErrorDialogMessage(message)
    setShowErrorDialog(true)
  }, [])

  /**
   * Close error dialog
   */
  const closeErrorDialog = useCallback(() => {
    setShowErrorDialog(false)
    setErrorDialogTitle('')
    setErrorDialogMessage('')
  }, [])

  /**
   * API call to init Automate Onboarding
   */
  const initAutomateOnboarding = () => {
    RegistrationApiService.initAzureAutomateOnboarding()
      .then(response => {
        const data = response['data'] ?? {}
        DebugService.log(`Azure Onboard Component: Init onboard data`, response)
        if (data['userCode']) {
          setDeviceCode(data['userCode'])
        }
        if (data['sessionId']) {
          setSessionId(data['sessionId'])
        }
        setIsValidating(false)
      })
      .catch(e => {
        showError('Initialization Failed', 'Failed to initialize Azure onboarding. Please refresh the page and try again.')
        DebugService.error(`Azure Onboard Component: Caught error while initiating onboarding`, e)
      })
      .finally(() => {
        setIsValidating(false)
      })
  }

  /**
   * Determine whether Submit button should be disabled
   */
  useEffect(() => {
    let isDisable = false

    // For manual method, check form completion on final step
    if (expanded === ONBOARDING_STEPS_CASE.MANUAL && completedStep === 2
      && linkForm['accountId'] === ''
      && linkForm['accountAliase'] === ''
      && linkForm['azClientId'] === ''
      && linkForm['azSubscriptionId'] === ''
      && linkForm['azClientSecret'] === ''
    ) {
      isDisable = true
    }

    // For automation method, check step-specific requirements
    if (expanded === ONBOARDING_STEPS_CASE.AUTOMATION) {
      if (completedStep === 0 && deviceCode === null) {
        initAutomateOnboarding()
      }

      // Step 1 requires confirmation checkbox to be checked
      if (completedStep === 0 && !isStep1Confirmed) {
        isDisable = true
      }
      // Step 2 requires subscription selection
      else if (completedStep === 1 && !selectedSubscription) {
        isDisable = true
      }
      // Step 3 requires account name to be provided
      else if (completedStep === 2 && (!accountName || accountName.trim() === '')) {
        isDisable = true
      }
      // Other steps don't require additional validation
    }

    setIsSubmitDisable(isDisable)
  }, [completedStep, linkForm, expanded, isStep1Confirmed, selectedSubscription, accountName])

  const getRoles = () => {
    RegistrationApiService.getAzureRoles()
      .then(response => {
        const data = response['data'] ?? []
        DebugService.log(`Azure Onboard Component: Roles`, response)
        if (data.length > 0) {
          setRoles(data)
        }
      })
      .catch(e => {
        DebugService.error(`Azure Onboard Component: Caught error while fetching roles`, e)
      })
      .finally(() => {})
  }
  
  useEffect(() => {
    getRoles()
  },[])

  return (
    <Container maxWidth="lg" className="onboarding-container">
      {/* Hero Section */}
      <div className="onboarding-hero">
        <div className="hero-background">
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge">
            <AzureIcon sx={{ fontSize: 20, mr: 1 }} />
            Microsoft Azure
          </div>
          <Typography variant="h2" className="hero-title">
            Connect Your <span className="gradient-text">Azure Account</span>
          </Typography>
          <Typography variant="h6" className="hero-subtitle">
            Seamlessly integrate your Azure subscription with our FinOps platform for comprehensive cost optimization and management.
          </Typography>

          {/* Progress Indicator */}
          <div className="progress-container">
            <div className="progress-info">
              <Typography variant="body2" className="progress-text">
                Setup Progress: {Math.round(((completedStep) / (expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? automationStepList.length : stepList.length)) * 100)}% Complete
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={((completedStep) / (expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? automationStepList.length : stepList.length)) * 100}
              className="modern-progress"
            />
          </div>
        </div>
      </div>

      {/* Method Selection */}
      <Fade in timeout={800}>
        <div className="method-selection">
          {/* <Typography variant="h4" className="section-title" sx={{ mb: 4, textAlign: 'center' }}>
            Choose Your Setup Method
          </Typography> */}

          {/* Automation Method Card */}
          <Card
            className={`method-card ${expanded === ONBOARDING_STEPS_CASE.AUTOMATION ? 'selected' : ''}`}
            onClick={() => handleChange(ONBOARDING_STEPS_CASE.AUTOMATION)}
          >
            <div className="method-header">
              <div className="method-icon">
                <AutomationIcon />
              </div>
              <div>
                <Typography variant="h5" className="method-title">
                  Automated Setup
                </Typography>
                <Chip label="Fast & Easy" size="small" color="success" sx={{ mt: 1 }} />
              </div>
            </div>
            <Typography className="method-description">
              One-click Azure Resource Manager (ARM) template deployment for instant integration.
              Perfect for quick setup with minimal manual configuration.
            </Typography>
            <ul className="method-features">
              <li>ARM template deployment</li>
              <li>Automatic role assignment</li>
              <li>Instant subscription linking</li>
              <li>5-minute setup time</li>
            </ul>
          </Card>

          {/* Manual Method Card */}
          <Card
            className={`method-card ${expanded === ONBOARDING_STEPS_CASE.MANUAL ? 'selected' : ''}`}
            onClick={() => handleChange(ONBOARDING_STEPS_CASE.MANUAL)}
          >
            <div className="method-header">
              <div className="method-icon">
                <ManualModeIcon />
              </div>
              <div>
                <Typography variant="h5" className="method-title">
                  Manual Setup
                </Typography>
                <Chip label="Full Control" size="small" color="primary" sx={{ mt: 1 }} />
              </div>
            </div>
            <Typography className="method-description">
              Step-by-step manual configuration for organizations with specific security requirements
              or custom Azure AD policies. Full control over every aspect of the integration.
            </Typography>
            <ul className="method-features">
              <li>Complete control over app registration</li>
              <li>Custom role assignment</li>
              <li>Detailed security review process</li>
              <li>Enterprise-grade compliance</li>
            </ul>
          </Card>
        </div>
      </Fade>

      {/* Manual Steps */}
      {expanded === ONBOARDING_STEPS_CASE.MANUAL && (
        <Zoom in timeout={600}>
          <div>
            {/* Modern Stepper */}
            <Card className="modern-stepper">
              <Stepper activeStep={completedStep} alternativeLabel>
                {stepList.map((step, index) => (
                  <Step key={step.id}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Card>

            {/* Step Content */}
            <Card className="step-content-card slide-in-up">
              <div className="step-header">
                <div className="step-icon">
                  {completedStep === 0 && <AppRegistrationIcon />}
                  {completedStep === 1 && <SecurityIcon />}
                  {completedStep === 2 && <LinkIcon />}
                </div>
                <Typography variant="h5" className="step-title">
                  {stepList[completedStep]?.label}
                </Typography>
              </div>
              <div className="step-content">
                {completedStep === 0 && <Step1Details />}
                {completedStep === 1 && <Step2Details roles={roles} />}
                {completedStep === 2 && <Step3Details onFormUpdate={onFormUpdateHandler} linkForm={linkForm} />}
              </div>
            </Card>

            {/* Account Link Form - Enhanced for Step 3 */}
            {completedStep === 2 && (
              <Card className="modern-form-container slide-in-up">
                <Typography variant="h6" sx={{ mb: 3, color: '#1e293b', fontWeight: 600 }}>
                  Connect Your Azure Account
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Account Name"
                      placeholder="My Azure Account"
                      value={linkForm.accountAliase}
                      onChange={(e) => onFormUpdateHandler({ ...linkForm, accountAliase: e.target.value })}
                      className="modern-form-field"
                      helperText="A friendly name for your Azure account"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Subscription ID"
                      placeholder="12345678-1234-1234-1234-123456789012"
                      value={linkForm.azSubscriptionId}
                      onChange={(e) => onFormUpdateHandler({ ...linkForm, azSubscriptionId: e.target.value })}
                      className="modern-form-field"
                      helperText="Your Azure subscription ID"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Application ID"
                      placeholder="87654321-4321-4321-4321-210987654321"
                      value={linkForm.azClientId}
                      onChange={(e) => onFormUpdateHandler({ ...linkForm, azClientId: e.target.value })}
                      className="modern-form-field"
                      helperText="Application (client) ID from your app registration"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Tenant ID"
                      placeholder="11111111-2222-3333-4444-555555555555"
                      value={linkForm.accountId}
                      onChange={(e) => onFormUpdateHandler({ ...linkForm, accountId: e.target.value })}
                      className="modern-form-field"
                      helperText="Directory (tenant) ID from your app registration"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      type="password"
                      label="Client Secret"
                      placeholder="Enter your client secret value"
                      value={linkForm.azClientSecret}
                      onChange={(e) => onFormUpdateHandler({ ...linkForm, azClientSecret: e.target.value })}
                      className="modern-form-field"
                      helperText="The secret value from your app registration certificates & secrets"
                    />
                  </Grid>
                </Grid>

                {APIErrorMessage.length > 0 && (
                  <div className="error-message">
                    {APIErrorMessage}
                  </div>
                )}
              </Card>
            )}

            {/* Action Buttons */}
            <Card className="modern-actions">
              <Button
                variant="outlined"
                disabled={completedStep === 0}
                onClick={() => backStepHandle(false)}
                className="modern-btn-back"
                startIcon={<ArrowForwardIosSharpIcon sx={{ transform: 'rotate(180deg)' }} />}
              >
                Back
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => nextStepHandle(false)}
                loading={isValidating}
                disabled={isSubmitDisable}
                loadingPosition="end"
                className="modern-btn-next"
                endIcon={<ArrowForwardIosSharpIcon />}
              >
                {completedStep === stepList.length - 1 ? 'Complete Setup' : 'Continue'}
              </LoadingButton>
            </Card>
          </div>
        </Zoom>
      )}

      {/* Automated Steps */}
      {expanded === ONBOARDING_STEPS_CASE.AUTOMATION && (
        <Zoom in timeout={600}>
          <div>
            {/* Modern Stepper for Automation */}
            <Card className="modern-stepper">
              <Stepper activeStep={completedStep} alternativeLabel>
                {automationStepList.map((step, index) => (
                  <Step key={step.id}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Card>

            {/* Automated Step Content */}
            <Card className="step-content-card slide-in-up">
              <div className="step-header">
                <div className="step-icon">
                  {completedStep === 0 && <DeploymentIcon />}
                  {completedStep === 1 && <ConfigurationIcon />}
                  {completedStep === 2 && <VerificationIcon />}
                </div>
                <Typography variant="h5" className="step-title">
                  {automationStepList[completedStep]?.label}
                </Typography>
              </div>
              <div className="step-content">
                {completedStep === 0 && <AutomatedStep1Details 
                deviceCode={deviceCode} 
                onConfirmationChange={handleStep1ConfirmationChange} 
                roles={roles}
                />}
                {completedStep === 1 && (
                  <AutomatedStep2Details
                    subscriptions={subscriptions}
                    selectedSubscription={selectedSubscription}
                    onSubscriptionSelect={handleSubscriptionSelect}
                    isLoading={isLoadingSubscriptions}
                  />
                )}
                {completedStep === 2 && (
                  <AutomatedStep3Details
                    accountData={accountData}
                    accountName={accountName}
                    onAccountNameChange={handleAccountNameChange}
                    isLoading={isLoadingAccountData}
                  />
                )}
              </div>
            </Card>

            {/* Action Buttons for Automation */}
            <Card className="modern-actions">
              <Button
                variant="outlined"
                disabled={completedStep === 0}
                onClick={() => backStepHandle(true)}
                className="modern-btn-back"
                startIcon={<ArrowForwardIosSharpIcon sx={{ transform: 'rotate(180deg)' }} />}
              >
                Back
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => nextStepHandle(true)}
                loading={isValidating}
                disabled={isSubmitDisable}
                loadingPosition="end"
                className="modern-btn-next"
                endIcon={<ArrowForwardIosSharpIcon />}
              >
                {completedStep === automationStepList.length - 1 ? 'Complete Automated Setup' : 'Continue'}
              </LoadingButton>
            </Card>
          </div>
        </Zoom>
      )}

      {/* Error Dialog */}
      <Dialog
        open={showErrorDialog}
        onClose={closeErrorDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: '#dc2626',
            fontWeight: 600,
            fontSize: '1.25rem'
          }}
        >
          <ErrorIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          {errorDialogTitle}
        </DialogTitle>
        <DialogContent
          sx={{
            paddingTop: '16px !important',
            paddingBottom: '24px'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#374151',
              lineHeight: 1.6,
              fontSize: '1rem'
            }}
          >
            {errorDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px 24px 24px',
            justifyContent: 'flex-end'
          }}
        >
          <Button
            onClick={closeErrorDialog}
            variant="contained"
            startIcon={<CloseIcon />}
            sx={{
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              color: 'white',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #b91c1c, #991b1b)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AzureOnboarding;